import Provider from '@/enums/Provider';
import UrlTypes from '@/enums/UrlTypes';
import ICategory from '@/interfaces/ICategory';
import IImage from '@/interfaces/IImage';
import IDescription from '@/interfaces/IDescription';
import { extractMetaData } from '@/helpers/extractMetaData';
import { extractCategoryName } from '@/helpers/category/extractCategoryName';
import { extractProductPaths } from '@/helpers/category/extractProductPaths/extractProductPaths';
import { extractCategoryDescription } from '@/helpers/category/description/extractCategoryDescription';
import { extractCategoryImage } from '@/helpers/category/extractCategoryImage';
import { extractImagesFromHtml } from '@/helpers/images/extractImagesFromHtml';
import { processHtml } from '@/helpers/html/htmlProcessor';
import { processImages } from '@/helpers/images/imageProcessor';

/**
 * Extracts category data from the provided HTML source and other inputs.
 * 
 * @param {string} htmlSource - The HTML source of the category page.
 * @param {string} pathname - The URL pathname of the category page.
 * @param {Provider} provider - The provider enum indicating the source of the data.
 * @param {string} joinedHubSrc - A concatenated string of all hub HTML for extracting images.
 * @param {string} baseUrl - The base URL for normalizing image and link paths.
 * @returns {ICategory} - An object containing metadata, name, images, product paths, and description.
 */
export function extractCategoryData(
    htmlSource: string,
    pathname: string,
    provider: Provider,
    joinedHubSrc: string,      // <- now a single string of all hub HTML
    baseUrl: string
): ICategory {
    // 1) metadata
    const { metaTitle, metaDescription } = extractMetaData(htmlSource, provider);

    // 2) name + product paths
    const name = extractCategoryName(htmlSource, pathname, provider);
    const productsPaths = extractProductPaths(htmlSource, provider);

    // 3) raw description HTML + its images
    const rawDesc = extractCategoryDescription(htmlSource, pathname, provider);
    const fallbackImages = extractImagesFromHtml(
        rawDesc.descriptionHtml,
        pathname,
        UrlTypes.CATEGORY_DESCRIPTION
    );
    const allRawImages = [...rawDesc.images, ...fallbackImages];

    // 4) clean HTML & normalize URLs
    const descriptionHtml = processHtml(rawDesc.descriptionHtml, allRawImages, baseUrl);
    const description: IDescription = {
        pathname,
        descriptionHtml,
        images: processImages(allRawImages, baseUrl),
    };

    // 5) hub‐specific category images (using the joined string)
    const rawCatImgs = joinedHubSrc
        ? extractCategoryImage(joinedHubSrc, pathname, provider)
        : [];
    const images: IImage[] = processImages(rawCatImgs, baseUrl);

    return {
        pathname,
        metaTitle,
        metaDescription,
        name,
        images,
        productsPaths,
        description,
    };
}
